import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import { getSidebarPanelOverlayTriggerProps } from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { Button } from '@heidi/design-system/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@heidi/design-system/command';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import {
  TypographyCaptionBold,
  TypographyFootNote,
} from '@heidi/design-system/typography';

import { HeidiTogetherBoxesSvg } from '@/assets/image/free-to-pay/heidi-together-boxes-svg';
import { IS_ASSISTANT_VIEW_ENABLED } from '@/components/constants/flags';
import { OfflineGatedTooltip } from '@/components/offline-recording/offline-gated-tooltip';
import { ASSISTANT_VIEW_SELECT_ID } from '@/features/actionCenter/constants';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { cn } from '@/lib/cn';
import { Card } from '@heidi/design-system/card';
import { Separator } from '@heidi/design-system/separator';
import { useTranslations } from '@heidi/runtime/i18n';
import { Check, Globe, UserRound } from 'lucide-react';
import { memo, useEffect, useRef } from 'react';
import { useReviewCombobox } from './hooks/useReviewCombobox';

export const SessionReviewComboBox = memo(() => {
  const {
    userData,
    selectedReviewUser,
    setSelectedReviewUser,
    isTogetherOrHigherTier,
    isCurrentUserAssistant,
    handleUpgradeClick,
    gpId,
    isGlobalAdmin,
    onSelectReviewUser,
    userList,
    titleText,
  } = useReviewCombobox();
  const { t } = useTranslations();
  const { shouldGateControls: isOfflineGated } = useOfflineGating();
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();

  const commandRef = useRef<HTMLDivElement | null>(null);

  // if refresh in other pages, dont have gpid, set the default selected user to the current user
  useEffect(() => {
    if (!gpId && !selectedReviewUser) {
      setSelectedReviewUser(userData?.kinde_user_id);
    }
  }, [selectedReviewUser, gpId, userData?.kinde_user_id, setSelectedReviewUser]);

  const reviewTrigger = (
    <Button
      data-testid="session-review-combobox-trigger"
      size="xxs"
      variant="secondary"
      disabled={isOfflineGated}
      className="hidden sm:flex"
    >
      <UserRound />
      {selectedReviewUser && <span className="truncate text-center">{titleText}</span>}
    </Button>
  );

  // Offline: reviewing another team member's sessions needs the server, so gate
  // the trigger. Match the button's `hidden sm:*` breakpoint on the wrapper span
  // so it doesn't take small-screen layout space while its child is hidden.
  if (isOfflineGated) {
    return (
      <OfflineGatedTooltip isOfflineGated className="hidden sm:inline-flex">
        {reviewTrigger}
      </OfflineGatedTooltip>
    );
  }

  return (
    <Popover>
      <PopoverTrigger
        {...getSidebarPanelOverlayTriggerProps(isSidebarPanelEnabled)}
        asChild
      >
        {reviewTrigger}
      </PopoverTrigger>
      <PopoverContent className="flex w-fit flex-col p-0" align="start">
        <Command className="rounded-md" ref={commandRef}>
          {isTogetherOrHigherTier && !isGlobalAdmin && (
            <CommandInput placeholder={t('sessionReview.searchUser')} />
          )}
          <CommandList className="relative">
            <CommandEmpty>{t('sessionReview.noUserFound')}</CommandEmpty>
            <CommandGroup>
              <div className="relative">
                {isTogetherOrHigherTier &&
                  isCurrentUserAssistant &&
                  IS_ASSISTANT_VIEW_ENABLED() && (
                    <>
                      <CommandItem
                        value={`${ASSISTANT_VIEW_SELECT_ID}-view-all`}
                        onSelect={() => {
                          onSelectReviewUser(ASSISTANT_VIEW_SELECT_ID);
                        }}
                      >
                        <Globe className="mr-2 size-4 shrink-0" aria-hidden />
                        {t('actionCenter.header')}
                        <Check
                          className={cn(
                            'ml-auto',
                            selectedReviewUser === ASSISTANT_VIEW_SELECT_ID
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                          size={16}
                        />
                      </CommandItem>
                      <Separator className="my-1" />
                    </>
                  )}
                {userList?.map((user, index) => (
                  <CommandItem
                    data-testid="session-review-combobox-member-item"
                    key={`${user.kinde_user_id}-${index}`}
                    value={`${user.name}-${index}`}
                    disabled={
                      !isTogetherOrHigherTier &&
                      user.kinde_user_id !== userData?.kinde_user_id
                    }
                    onSelect={() => {
                      onSelectReviewUser(user.kinde_user_id);
                    }}
                  >
                    {user.kinde_user_id === userData?.kinde_user_id ? 'Me' : user.name}
                    <Check
                      className={cn(
                        'ml-auto',
                        selectedReviewUser === user.kinde_user_id
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                      size={16}
                    />
                  </CommandItem>
                ))}
              </div>
            </CommandGroup>
            {(!isTogetherOrHigherTier || isGlobalAdmin) && (
              <div className="absolute inset-x-0 bottom-0 size-full bg-gradient-to-t from-surface-elevated to-transparent" />
            )}
          </CommandList>
        </Command>

        {isGlobalAdmin && (
          <div className={`relative z-50 flex h-fit justify-center p-3`}>
            <Card
              className={`w-[180px] rounded-md border-none bg-gradient-to-r from-background-secondary-hover to-text-disabled p-0.5 shadow-lg`}
            >
              <div className="flex flex-col items-center justify-center gap-3 rounded-md bg-surface p-3">
                <div className="flex flex-col gap-1">
                  <TypographyCaptionBold className="text-center">
                    {t('sessionReview.seeTeamSessions')}
                  </TypographyCaptionBold>
                  <TypographyFootNote className="text-center text-text-secondary">
                    {t('sessionReview.sessionReviewNotAvailableForGlobalAdmins')}
                  </TypographyFootNote>
                </div>

                <TypographyCaptionBold>
                  {t('sessionReview.seeTeamSessionsComingSoon')}
                </TypographyCaptionBold>
              </div>
            </Card>
          </div>
        )}
        {!isTogetherOrHigherTier && (
          <div className={`relative z-50 flex h-fit justify-center p-3`}>
            <Card
              className={`w-[180px] rounded-md border-none bg-tier-practice p-0.5 shadow-lg`}
            >
              <div className="flex flex-col items-center justify-center gap-3 rounded-md bg-surface p-3">
                <div className="flex flex-col gap-1">
                  <TypographyCaptionBold className="text-center">
                    {t('sessionReview.seeTeamSessions')}
                  </TypographyCaptionBold>
                  <TypographyFootNote className="text-center text-text-secondary">
                    {t('sessionReview.collaborateTeamNotes')}
                  </TypographyFootNote>
                </div>
                <Button
                  disableAutomaticSvgSizing
                  className="flex max-w-[180px] gap-2 px-2.5 py-1.5"
                  variant={'pro_plus'}
                  onClick={handleUpgradeClick}
                  size={'xs'}
                >
                  <HeidiTogetherBoxesSvg alt="logo" className="justify-self-center" />
                  <TypographyCaptionBold>
                    {t('sessionReview.upgradeToPractice')}
                  </TypographyCaptionBold>
                </Button>
              </div>
            </Card>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
});

SessionReviewComboBox.displayName = 'SessionReviewComboBox';
